/**
 * Human Input Form — War Room Phase C.5
 *
 * Form for accountants/attorneys to submit their opinion on a disputed issue.
 * Submits via submitHumanInput() API call. Shows loading state during
 * re-analysis trigger.
 */

import React, { useState } from 'react';
import { Send, X, Loader2, CheckCircle } from 'lucide-react';
import { submitHumanInput } from '@/services/deliberationApi';

// =============================================================================
// TYPES
// =============================================================================

export interface HumanInputFormProps {
  reportId: string;
  issueTag: string;
  onSubmitted?: () => void;
  onCancel?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function HumanInputForm({
  reportId,
  issueTag,
  onSubmitted,
  onCancel,
}: HumanInputFormProps) {
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorRole, setAuthorRole] = useState('human:accountant');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await submitHumanInput(reportId, {
        issue_tag: issueTag,
        content: content.trim(),
        author_role: authorRole,
        author_name: authorName.trim() || 'Anonymous',
      });
      setContent('');
      setAuthorName('');
      setSuccess(true);
      onSubmitted?.();
      // Auto-dismiss success after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit input');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--color-needs-human-tint)] rounded-lg border border-[color:var(--color-needs-human-tint-bd)] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Add Expert Input</h4>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-[color:var(--color-text-dim)] hover:text-[color:var(--color-text-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Role selector */}
      <div className="flex gap-2">
        {[
          { value: 'human:accountant', label: 'Accountant' },
          { value: 'human:attorney', label: 'Attorney' },
          { value: 'human:adjuster', label: 'Adjuster' },
        ].map((role) => (
          <button
            key={role.value}
            type="button"
            onClick={() => setAuthorRole(role.value)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              authorRole === role.value
                ? 'bg-[var(--color-brand)] text-[color:var(--color-on-brand)] border-[color:var(--color-brand)]'
                : 'bg-[var(--color-bg-surface)] text-[color:var(--color-text-secondary)] border-[color:var(--color-border)] hover:border-[color:var(--color-brand)]'
            }`}
          >
            {role.label}
          </button>
        ))}
      </div>

      {/* Name */}
      <input
        type="text"
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
        placeholder="Your name (optional)"
        className="w-full px-3 py-2 text-sm border border-[color:var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand)] focus:border-transparent"
      />

      {/* Content */}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Share your expert opinion on this issue..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-[color:var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand)] focus:border-transparent resize-none"
        disabled={isSubmitting}
      />

      {/* Error */}
      {error && (
        <p className="text-xs text-[color:var(--color-needs-human-text)]">{error}</p>
      )}

      {/* Success confirmation */}
      {success && (
        <div className="flex items-center gap-2 px-3 py-2 bg-transparent border border-[color:var(--color-verified)] rounded-lg">
          <CheckCircle className="h-4 w-4 text-[color:var(--color-verified)] flex-shrink-0" />
          <p className="text-xs text-[color:var(--color-verified)]">
            Input submitted — agents have re-analyzed this issue.
          </p>
        </div>
      )}

      {/* Submit */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[color:var(--color-needs-human-text)]">
          {isSubmitting
            ? 'Submitting and triggering re-analysis...'
            : 'Your input will trigger AI agents to re-analyze this issue.'}
        </p>
        <button
          type="submit"
          disabled={isSubmitting || !content.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[color:var(--color-on-brand)] bg-[var(--color-brand)] rounded-lg hover:bg-[var(--color-brand-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Submit
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default HumanInputForm;
